import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Calendar,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useBranding } from '@/hooks/useBranding';
import { t } from '@/i18n';

interface SidebarItemProps {
  icon: React.ElementType;
  label: string;
  to: string;
  collapsed: boolean;
  badge?: string | number;
  onClick?: () => void;
}

const SidebarItem = ({ icon: Icon, label, to, collapsed, badge, onClick }: SidebarItemProps) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  const content = (
    <Link to={to} className="block" onClick={onClick}>
      <Button
        variant={isActive ? 'secondary' : 'ghost'}
        className={cn(
          'w-full justify-start gap-3 transition-all',
          collapsed && 'justify-center px-2',
          isActive && 'bg-secondary font-medium'
        )}
        size={collapsed ? 'icon' : 'default'}
      >
        <Icon className={cn('h-5 w-5', !collapsed && 'shrink-0')} />
        {!collapsed && <span className="flex-1 text-left">{label}</span>}
        {!collapsed && badge && (
          <span className="ml-auto text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5">
            {badge}
          </span>
        )}
      </Button>
    </Link>
  );

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            {label}
            {badge && (
              <span className="text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5">
                {badge}
              </span>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const Sidebar = ({ collapsed, onToggle }: SidebarProps) => {
  const { user } = useAuthStore();
  const { branding } = useBranding();
  const isAdmin = user?.role === 'ADMIN';

  // Close sidebar on mobile after navigation
  const handleItemClick = () => {
    if (window.innerWidth < 1024 && !collapsed) {
      onToggle();
    }
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r bg-background transition-all duration-300',
        'lg:relative lg:z-auto', // Relative positioning on desktop
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex h-16 items-center justify-center border-b px-4">
          <Link to="/dashboard" className="flex items-center justify-center">
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt={branding.appName}
                className={cn(
                  "object-contain transition-all",
                  collapsed ? "h-10 w-30" : "h-12 w-36"
                )}
              />
            ) : (
              <LayoutDashboard className={cn(
                "text-primary transition-all",
                collapsed ? "h-6 w-6" : "h-8 w-8"
              )} />
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-2 pt-4 overflow-y-auto">
          <SidebarItem
            icon={LayoutDashboard}
            label={t('dashboard.title')}
            to="/dashboard"
            collapsed={collapsed}
            onClick={handleItemClick}
          />
          <SidebarItem
            icon={Calendar}
            label="Historia spotkań"
            to="/history"
            collapsed={collapsed}
            onClick={handleItemClick}
          />
          <SidebarItem
            icon={FileText}
            label="Nowe spotkanie"
            to="/new-meeting"
            collapsed={collapsed}
            onClick={handleItemClick}
          />
          {isAdmin && (
            <SidebarItem
              icon={Settings}
              label="Admin Panel"
              to="/admin"
              collapsed={collapsed}
              onClick={handleItemClick}
            />
          )}
        </nav>

        {/* User section */}
        <div className="border-t p-2 space-y-3 pb-20 lg:pb-2">
          {/* Logo for mobile (visible when sidebar is expanded) */}
          {!collapsed && branding.logoUrl && (
            <div className="flex justify-center px-3 py-2 lg:hidden">
              <img
                src={branding.logoUrl}
                alt={branding.appName}
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* User info */}
          {!collapsed && user && (
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/50">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <User className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.fullName}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          )}
          {collapsed && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center h-10 w-10 mx-auto rounded-full bg-primary text-primary-foreground cursor-pointer">
                    <User className="h-5 w-5" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <div className="text-sm">
                    <p className="font-medium">{user?.fullName}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Collapse toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="w-full mt-2"
            onClick={onToggle}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </aside>
  );
};
